import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Week close (declutter ★1): every café's week on one screen — punch
// issues, cash, certification, next week's schedule — and ONE confirmed
// action to certify all ready cafés, instead of the old
// switch-café → review → type CERTIFY loop repeated four times.

function mondayOf(offsetWeeks = 0) {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - dow + offsetWeeks * 7);
  return d.toISOString().slice(0, 10);
}

const Dot = ({ ok, warn, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5 }}>
    <span style={{ width: 9, height: 9, borderRadius: 999,
      background: ok ? '#2e7d32' : warn ? '#e6a23c' : '#c0c4cc' }} />
    {label}
  </span>
);

export function WeekCloseView({ user }) {
  const headers = { 'x-user-id': String(user.id) };
  const [weekStart, setWeekStart] = useState(mondayOf(-1)); // default: last week (the one you close)
  const [board, setBoard] = useState(null);
  const [err, setErr] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  async function load() {
    setErr('');
    try {
      const r = await api('GET', `/api/admin/week-close?week_start=${weekStart}`, null, headers);
      setBoard(r.locations);
    } catch (e) { setErr(e?.message || String(e)); setBoard([]); }
  }
  useEffect(() => { setResults(null); load(); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = (board || []).filter((l) => l.ready_to_certify);
  const go = (tab) => window.dispatchEvent(new CustomEvent('monarch:navigate', { detail: tab }));

  async function certifyAll() {
    setBusy(true); setErr('');
    try {
      const r = await api('POST', '/api/admin/week-close/certify',
        { week_start: weekStart, location_keys: ready.map((l) => l.location_key) }, headers);
      setResults(r.results);
      setConfirming(false); setTyped('');
      await load();
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  const weekLabel = (ws) => {
    const d = new Date(ws + 'T12:00:00');
    const end = new Date(d); end.setDate(end.getDate() + 6);
    const f = (x) => x.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${f(d)} – ${f(end)}`;
  };

  return (
    <div className="space-y-3">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-base font-semibold">Week close</div>
            <div className="text-xs text-gray-500">
              All cafés, one screen: fix what's open, then certify every ready store in one step.
            </div>
          </div>
          <select value={weekStart} onChange={(e) => setWeekStart(e.target.value)}
            style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 6, background: '#fff' }}>
            {[-1, -2, -3, 0].map((w) => (
              <option key={w} value={mondayOf(w)}>
                {weekLabel(mondayOf(w))}{w === 0 ? ' (this week — in progress)' : w === -1 ? ' (last week)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {err && <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-xs text-red-700">{err}</div></div>}

      {board === null && <div className="card text-sm text-gray-500">Loading…</div>}

      {(board || []).map((l) => (
        <div key={l.location_id} className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
          <div style={{ minWidth: 110, fontWeight: 700 }}>{l.location_name}</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flex: 1 }}>
            <Dot ok={l.open_punch_issues === 0} warn={l.open_punch_issues > 0}
              label={l.open_punch_issues === 0 ? 'Punches clean' : `${l.open_punch_issues} punch issue${l.open_punch_issues === 1 ? '' : 's'}`} />
            <Dot ok={l.cash_certified} warn={l.cash_entered}
              label={l.cash_certified ? 'Cash certified' : l.cash_entered ? 'Cash entered' : 'No cash count'} />
            <Dot ok={l.certified}
              label={l.certified ? `Certified · ${l.certified_by || ''}` : 'Not certified'} />
            <Dot ok={l.next_week_published_shifts > 0}
              label={l.next_week_published_shifts > 0
                ? `Next week: ${l.next_week_published_shifts} shifts published`
                : 'Next week not published'} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {l.open_punch_issues > 0 && (
              <button onClick={() => go('sched-time')} className="btn" style={{ fontSize: 11.5 }}>Fix punches</button>
            )}
            {!l.cash_entered && (
              <button onClick={() => go('reports-cash-counts')} className="btn" style={{ fontSize: 11.5 }}>Enter cash</button>
            )}
            {l.next_week_published_shifts === 0 && (
              <button onClick={() => go('sched-build')} className="btn" style={{ fontSize: 11.5 }}>Build schedule</button>
            )}
          </div>
        </div>
      ))}

      {results && (
        <div className="card">
          <div className="text-sm font-semibold mb-1">Certify results</div>
          {results.map((r) => (
            <div key={r.location_key} style={{ fontSize: 12.5, padding: '2px 0' }}>
              {r.ok ? '✅' : '⚠️'} {r.location_key.replace(/_/g, ' ')}
              {r.ok ? (r.alreadyCertified ? ' — already certified' : ' — certified') : ` — ${r.error}`}
            </div>
          ))}
        </div>
      )}

      {board && ready.length > 0 && !confirming && (
        <button onClick={() => setConfirming(true)} className="btn-primary" style={{ padding: '10px 18px' }}>
          Certify {ready.length} ready caf{ready.length === 1 ? 'é' : 'és'} ({ready.map((l) => l.location_name).join(', ')})
        </button>
      )}
      {board && ready.length === 0 && !board.every((l) => l.certified) && (
        <div className="text-xs text-gray-500">
          No café is ready yet — a store needs zero punch issues and a cash count entered before it can certify.
        </div>
      )}
      {board && board.length > 0 && board.every((l) => l.certified) && (
        <div className="text-sm" style={{ color: '#2e7d32', fontWeight: 600 }}>
          ✓ Every café is certified for this week.
        </div>
      )}

      {confirming && (
        <div className="card" style={{ borderLeft: '4px solid #b48a47' }}>
          <div className="text-sm mb-2">
            Certifying <b>{ready.map((l) => l.location_name).join(', ')}</b> for {weekLabel(weekStart)}.
            This runs the same certification as the per-café flow (reliability + PTO accrual included).
            Type <b>CERTIFY</b> to confirm — once for the whole batch.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="CERTIFY"
              style={{ padding: '8px 10px', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6, width: 140 }} />
            <button onClick={certifyAll} disabled={typed !== 'CERTIFY' || busy} className="btn-primary">
              {busy ? 'Certifying…' : 'Confirm'}
            </button>
            <button onClick={() => { setConfirming(false); setTyped(''); }} className="btn">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
